import React, { Component, PropTypes } from 'react'
import Transition from 'react-motion-ui-pack'
import { Motion, spring } from 'react-motion'
import Measure from 'react-measure'
import throttle from 'lodash/throttle'

import MouseEvents from './utility/MouseEvents'

const getStyles = ({ userInteractable }, state) => ({
  root: {
    overflow: 'hidden',
    cursor: userInteractable && 'ew-resize'
  },
  inner: {
    display: 'flex',
    flexDirection: 'row'
  },
  image: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 99,

    current: {
      zIndex: 100
    }
  }
})

const MOVE_THRESHOLD = 0.7
const LEAVE_INTERVAL = 100

const getTranslateStyle = ({ x }) => ({
  transform: `translateX(${x}px)`
})

class Gallery extends Component {
  timer = null
  raf = null

  constructor (props) {
    super(props)
    this.state = {
      itemWidth: 0,
      currentImage: props.currentImage,
      isTransitioning: false
    }
  }

  // Lifecycle

  componentDidMount () {
    if (this.props.auto) { this.startTimer() }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.currentImage !== this.props.currentImage) {
      this.setState({ currentImage: this.props.currentImage })
    }

    if (prevProps.auto !== this.props.auto) {
      this.props.auto ? this.startTimer() : this.stopTimer()
    }
  }

  componentWillUnmount () {
    this.stopTimer()
    cancelAnimationFrame(this.raf)
  }

  // Actions

  startTimer () {
    if (!this.props.auto) { return }
    this.stopTimer()
    this.timer = setTimeout(this.tick, this.props.interval)
  }

  stopTimer () {
    clearTimeout(this.timer)
    cancelAnimationFrame(this.raf)
    this.timer = null
  }

  tick = () => {
    const nextImage = this.isLastImage ? 0 : this.state.currentImage + 1
    this.setState({ currentImage: nextImage, isTransitioning: true })
  }

  // Handlers

  handleLeave = throttle(({ opacity }) => {
    this.raf = requestAnimationFrame(() => {
      this.setState({ isTransitioning: false })
      this.startTimer()
    })
  }, LEAVE_INTERVAL, { leading: true })

  handleMouseMove = ({ velocityX, velocityY }) => {
    const didMoveX = Math.abs(velocityX) > MOVE_THRESHOLD
    const didMovePositiveX = velocityX > 0
    const didMoveNegativeX = velocityX < 0

    this.stopTimer()

    if (didMovePositiveX) {
      this.setState({ currentImage: this.nextImageIndex })
    } else if (didMoveNegativeX) {
      this.setState({ currentImage: this.previousImageIndex })
    }
  }

  handleMouseOver = () => {
    this.stopTimer()
  }

  handleMouseOut = () => {
    this.startTimer()
  }

  // Getters

  get isFirstImage () {
    return this.state.currentImage == 0
  }

  get isLastImage () {
    return this.state.currentImage == this.props.images.length - 1
  }

  get previousImageIndex () {
    return this.isFirstImage ? this.props.images.length - 1 : this.state.currentImage - 1
  }

  get nextImageIndex () {
    return this.isLastImage ? 0 : this.state.currentImage + 1
  }

  get previousImage () {
    return this.props.images[this.previousImageIndex]
  }

  get currentImage () {
    return this.props.images[this.state.currentImage]
  }

  get nextImage () {
    return this.props.images[this.nextImageIndex]
  }

  get defaultTransitionStyle () {
    return { opacity: 0 }
  }

  get transitionStyle () {
    const { springConfig } = this.props
    const { isTransitioning } = this.state
    return isTransitioning ? { opacity: spring(1.0, springConfig) } : this.defaultTransitionStyle
  }

  // Rendering

  render () {
    const {
      images,
      currentImage,
      userInteractable,
      springConfig,
      style,
    } = this.props
    const styles = getStyles(this.props, this.state)

    return (
      <div style={styles.root}>
        <MouseEvents
          enabled={userInteractable}
          onMouseOver={this.handleMouseOver}
          onMouseMove={this.handleMouseMove}
          onMouseOut={this.handleMouseOut}
        >
          <Transition
            runOnMount={false}
            enter={{
              opacity: 1,
            }}
            leave={{
              opacity: 0,
            }}
            onLeave={this.handleLeave}
          >
            <img
              key={this.currentImage}
              src={this.currentImage}
              style={styles.image}
            />
          </Transition>
        </MouseEvents>
      </div>
    )

  }
}

Gallery.propTypes = {
  images: PropTypes.array,
  auto: PropTypes.bool,
  userInteractable: PropTypes.bool,
  interval: PropTypes.number,
  springConfig: PropTypes.shape({
    stiffness: PropTypes.number,
    damping: PropTypes.number,
  })
}

Gallery.defaultProps = {
  images: [],
  currentImage: 0,
  auto: true,
  userInteractable: true,
  interval: 2.0 * 1000, // seconds * miliseconds
  springConfig: {
    stiffness: 150,
    damping: 13
  }
}

export default Gallery



// WEBPACK FOOTER //
// ./src/components/Gallery.js